import { Check } from "lucide-react";
import { useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Kbd } from "../../layout/Kbd";
import type { ContactType } from "../../types";

// «Registrer kontakt» inline (fiks 1): raden utvides til et lite skjema.
// Én lagring gir én kontakt i loggen og høyst ett neste steg. Ctrl+Enter
// lagrer, Esc avbryter, og fokus starter i Notat.

export type ContactDraft = {
  type: Exclude<ContactType, "other">;
  note: string;
  nextStep: string;
  due: string;
};

export const emptyContactDraft = (): ContactDraft => ({
  type: "call",
  note: "",
  nextStep: "",
  due: "",
});

const TYPES: { value: ContactDraft["type"]; label: string }[] = [
  { value: "call", label: "Ring" },
  { value: "meeting", label: "Møte" },
  { value: "email", label: "E-post" },
];

/** E-post krever emne eller notat; Ring og Møte kan stå tomme. */
export const contactDraftError = (draft: ContactDraft) =>
  draft.type === "email" && !draft.note.trim()
    ? "Skriv emne eller notat for e-post."
    : null;

export const ContactForm = ({
  id,
  draft,
  onChange,
  onSave,
  onCancel,
  error,
  isDeal,
}: {
  id: string;
  draft: ContactDraft;
  onChange: (draft: ContactDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  /** Feilen fra forrige forsøk, med F-referanse. */
  error?: string | null;
  /** Neste steg kobles til muligheten. */
  isDeal: boolean;
}) => {
  const noteRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    noteRef.current?.focus();
  }, []);
  const invalid = contactDraftError(draft);
  const set = (change: Partial<ContactDraft>) =>
    onChange({ ...draft, ...change });

  return (
    <form
      aria-label="Registrer kontakt"
      className="flex flex-col gap-3 border-t bg-muted/20 px-4 py-4 sm:pl-11"
      onSubmit={(event) => {
        event.preventDefault();
        if (!invalid) onSave();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        } else if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          event.stopPropagation();
          if (!invalid) onSave();
        }
      }}
    >
      <div className="grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)]">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs font-normal text-muted-foreground">
            Type
          </Label>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={draft.type}
            onValueChange={(v) => v && set({ type: v as ContactDraft["type"] })}
            aria-label="Type kontakt"
          >
            {TYPES.map((t) => (
              <ToggleGroupItem key={t.value} value={t.value} className="px-3">
                {t.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label
            htmlFor={`${id}-note`}
            className="text-xs font-normal text-muted-foreground"
          >
            Notat{draft.type === "email" ? " (emne eller notat)" : ""}
          </Label>
          <Textarea
            id={`${id}-note`}
            ref={noteRef}
            rows={2}
            value={draft.note}
            onChange={(e) => set({ note: e.target.value })}
            aria-invalid={!!invalid && draft.note.length > 0 ? true : undefined}
            className="min-h-[60px] text-[13px]"
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_160px] sm:pl-0">
        <div className="flex flex-col gap-1.5">
          <Label
            htmlFor={`${id}-step`}
            className="text-xs font-normal text-muted-foreground"
          >
            Neste steg{isDeal ? " på muligheten" : ""}
          </Label>
          <Input
            id={`${id}-step`}
            value={draft.nextStep}
            onChange={(e) => set({ nextStep: e.target.value })}
            placeholder="Valgfritt"
            className="h-9 text-[13px]"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label
            htmlFor={`${id}-due`}
            className="text-xs font-normal text-muted-foreground"
          >
            Dato
          </Label>
          <DatePicker
            id={`${id}-due`}
            value={draft.due}
            onChange={(due) => set({ due })}
            placeholder="Velg dato"
            disabled={!draft.nextStep.trim()}
          />
        </div>
      </div>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <p className="min-w-0 flex-1 text-[11.5px] text-muted-foreground">
          Lagres som kontakt på kunden
          {draft.nextStep.trim() ? " og som neste steg" : ""}. Oppdaterer «Sist
          kontakt».
        </p>
        <span className="hidden items-center gap-1 text-[11.5px] text-muted-foreground md:flex">
          <Kbd>Ctrl</Kbd>
          <Kbd>Enter</Kbd> lagre
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Avbryt
        </Button>
        <Button type="submit" size="sm" disabled={!!invalid}>
          <Check aria-hidden className="size-4" />
          Lagre kontakt
        </Button>
      </div>
    </form>
  );
};
